// Invariant parse/format for a money/decimal text input (#204's DeviceSettingType.Decimal control, and the
// booking-reference spending-limit editor). Deliberately NOT locale-aware: this codebase has a documented,
// real historical bug where a form-bound `double` field parsed "0.4" as `4` under `de-DE` (see the root
// CLAUDE.md conventions and docs/articles/devices/pos-app-configuration.md#decimal). A JSON wire value is
// always a genuine number with no locale, so the one place a locale can leak in is a text box — this helper is
// that one place, and it never delegates to `Number.prototype.toLocaleString`/`Intl.NumberFormat`/a culture
// object (see `helpers/numeric-helper.ts`'s `Separators`, which IS locale-aware and must not be reused here).
//
// A German user types a comma ("12,50"); the wire must carry 12.5 (a real JSON number). Accepting both '.' and
// ',' as the decimal separator on input, while always formatting back out with '.', keeps every consumer on
// the invariant side of that boundary.

/**
 * Parses a user-typed decimal string into a number, accepting either '.' or ',' as the decimal separator.
 * Returns `null` for an empty/whitespace-only input or anything that isn't a valid non-negative decimal
 * (matching the backend's `Decimal` validation — negative amounts and anything else are rejected there too).
 */
export function parseInvariantDecimal(input: string | null | undefined): number | null {
  if (input === null || input === undefined) return null;
  const trimmed = input.trim();
  if (trimmed === '') return null;

  // Exactly one optional leading '-' (rejected below, kept in the regex only so a bare "-" doesn't parse as 0
  // via a partial match), digits, and an optional single fractional part after '.' or ','.
  const normalized = trimmed.replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;

  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}

/** Formats a decimal value for display in the invariant control — plain `'.'`-separated, never localized. */
export function formatInvariantDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * Parses a user-typed article price, accepting either '.' or ',' as the decimal separator (#809).
 *
 * A sibling of {@link parseInvariantDecimal} rather than an option on it, because the two answer different
 * questions. A spending limit or a device setting can never be negative, and their callers rely on that refusal.
 * An article price can: neither `PriceChangeComposition.Price` nor `NewCompositionOverride.Price` carries any
 * validation server-side, and a deposit return ("Pfand zurück", -0,50 €) is a plausible article. So this one
 * accepts a single leading minus.
 *
 * It also refuses more than two decimal places instead of rounding them away. A price is money: silently turning
 * "3,499" into 3.50 would save a number the person never typed, which is the very class of bug #809 is about
 * ("12,50" saved as 12). A thousands separator ("1.234,50") is refused too, because it cannot be told apart from
 * a decimal point, and an article price does not reach four digits in practice.
 *
 * @returns the price, or `null` for an empty input or anything that is not a plain decimal with at most two
 *   decimal places. `-0` comes back as `0`.
 */
export function parsePriceInput(input: string | null | undefined): number | null {
  if (input === null || input === undefined) return null;
  const trimmed = input.trim();
  if (trimmed === '') return null;
  if (!/^-?\d+([.,]\d{1,2})?$/.test(trimmed)) return null;

  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  // `Number('-0')` is -0, which JSON sends as 0 but which `Object.is` (and so a spec's `toBe(0)`) tells apart.
  return value === 0 ? 0 : value;
}

/**
 * Formats a price for the price input the way a German reader writes it: always two decimals, comma separated
 * (`3.5` becomes `"3,50"`).
 *
 * Unlike {@link formatInvariantDecimal}, which pre-fills an admin setting with the invariant `"12.5"`, this one
 * pre-fills a field that sits next to prices the page shows as "3,50 €". A `"3.50"` there reads like a different
 * number format. It is still built by hand rather than through `Intl`, so the output never depends on the
 * browser's locale and {@link parsePriceInput} always reads it back.
 */
export function formatPriceInput(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return value.toFixed(2).replace('.', ',');
}
